/**
 * Subscribe / one-time switching, shared by the cart page and the cart drawer.
 *
 * This started life inside section-cart.js. The drawer needs the same control
 * on every page of the store, not just /cart, so it moved to a component-*
 * file when its scope widened rather than keeping a section-scoped name.
 *
 * Both consumers load this directly; the customElements guard makes a double
 * load harmless.
 *
 * Shopify's Cart AJAX API takes the selling plan as a `selling_plan` field on
 * /cart/change.js, so switching between one-time and Subscribe & Save needs no
 * subscription app. The API requires the line to be identified by `line`
 * whenever selling_plan is present.
 */

/** Line wrappers differ between the page (.cart-line) and the drawer (.cd-line). */
function lineRoot(element) {
  return element?.closest('.cart-line, .cd-line');
}

function setBusy(element, busy) {
  const line = lineRoot(element);
  if (line) line.setAttribute('aria-busy', busy ? 'true' : 'false');
}

function reportError(element, error) {
  const target = lineRoot(element)?.querySelector('.cart-line__error');
  if (target) target.textContent = error?.description || error?.message || '';
}

function changeSellingPlan(line, sellingPlan, source) {
  const cart = window.Cart;
  if (!cart) return Promise.resolve();

  const quantity = cart.state?.items?.[line - 1]?.quantity ?? 1;
  return cart.change({ line, quantity, selling_plan: sellingPlan }, source);
}

if (!customElements.get('cart-purchase-mode')) {
  customElements.define(
    'cart-purchase-mode',
    class CartPurchaseMode extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        const option = event.target.closest('.cart-mode__option, .cd-modes__option');
        if (!option || option.getAttribute('aria-pressed') === 'true') return;

        const line = parseInt(this.dataset.cartLine, 10);
        if (!line) return;

        setBusy(this, true);
        changeSellingPlan(line, option.dataset.sellingPlan || '', option).catch((error) => {
          setBusy(this, false);
          reportError(this, error);
        });
      }
    },
  );
}

/* The frequency select and the "switch to Subscribe & Save" nudge both change a
   selling plan, so they share one delegated pair of listeners. */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-cart-frequency]');
  if (!select) return;

  const line = parseInt(select.dataset.cartLine, 10);
  if (!line) return;

  setBusy(select, true);
  changeSellingPlan(line, select.value, select).catch((error) => {
    setBusy(select, false);
    reportError(select, error);
  });
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-switch-plan]');
  if (!button) return;

  const line = parseInt(button.dataset.cartLine, 10);
  if (!line) return;

  event.preventDefault();
  setBusy(button, true);
  changeSellingPlan(line, button.dataset.cartSwitchPlan, button).catch((error) => {
    setBusy(button, false);
    reportError(button, error);
  });
});
